import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CallBackProps, EVENTS, Locale, STATUS, Step } from 'react-joyride';
import { AI_GENERATION_TOUR_CARD_SCROLL_OFFSET, AI_GENERATION_TOUR_START_DELAY_MS } from './AIGenerationTour.constants';
import {
    AIGenerationTourStepData,
    handleTourStepScroll,
    hasAIGenerationTourRecord,
    setAIGenerationTourCompleted,
} from './AIGenerationTour.helper';
import { buildAIGenerationTourSteps } from './AIGenerationTour.steps';

interface UseAIGenerationTourOptions {
    cardCount: number;
    ensureCardExists: () => void;
    scrollContainerRef: RefObject<HTMLElement | null>;
}

interface UseAIGenerationTourResult {
    runTour: boolean;
    startTour: () => void;
    steps: Step[];
    locale: Locale;
    onCallback: (data: CallBackProps) => void;
}

export const useAIGenerationTour = ({
    cardCount,
    ensureCardExists,
    scrollContainerRef,
}: UseAIGenerationTourOptions): UseAIGenerationTourResult => {
    const { t } = useTranslation();
    const [runTour, setRunTour] = useState(false);
    const [pendingTourStart, setPendingTourStart] = useState(false);
    const autoTourCheckedRef = useRef(false);

    const steps = useMemo(() => buildAIGenerationTourSteps(t), [t]);

    const locale = useMemo<Locale>(
        () => ({
            back: t('mannequin.aiGeneration.tour.back'),
            close: t('mannequin.aiGeneration.tour.close'),
            last: t('mannequin.aiGeneration.tour.finish'),
            next: t('mannequin.aiGeneration.tour.next'),
            skip: t('mannequin.aiGeneration.tour.skip'),
        }),
        [t]
    );

    const startTour = useCallback(() => {
        if (cardCount === 0) {
            ensureCardExists();
            setPendingTourStart(true);
            return;
        }
        setRunTour(true);
    }, [cardCount, ensureCardExists]);

    useEffect(() => {
        if (!pendingTourStart || cardCount === 0) return;

        const timer = window.setTimeout(() => {
            setPendingTourStart(false);
            setRunTour(true);
        }, AI_GENERATION_TOUR_START_DELAY_MS);

        return () => window.clearTimeout(timer);
    }, [pendingTourStart, cardCount]);

    useEffect(() => {
        if (autoTourCheckedRef.current) return;
        autoTourCheckedRef.current = true;

        hasAIGenerationTourRecord().then((hasRecord) => {
            if (!hasRecord) {
                startTour();
            }
        });
    }, [startTour]);

    const onCallback = useCallback(
        (data: CallBackProps) => {
            const { status, type, step, index } = data;

            if (type === EVENTS.STEP_BEFORE && step.target) {
                const selector = typeof step.target === 'string' ? step.target : '';
                const scrollOffset =
                    (step.data as AIGenerationTourStepData | undefined)?.scrollOffset ??
                    AI_GENERATION_TOUR_CARD_SCROLL_OFFSET;

                window.requestAnimationFrame(() => {
                    handleTourStepScroll(index, selector, scrollOffset, scrollContainerRef);
                });
            }

            if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
                setRunTour(false);
                setAIGenerationTourCompleted().catch(console.error);
            }
        },
        [scrollContainerRef]
    );

    return {
        runTour,
        startTour,
        steps,
        locale,
        onCallback,
    };
};
